<template>
  <section id="clusters-index">
    <Breadcrumbs :breadcrumbs="breadcrumbs">
      <div slot="breadcrumbAfter" class="d-flex align-items-center">
        <Searchable class="mr-2" :querySearch.sync="querySearch"/>
      </div>
    </Breadcrumbs>

    <div class="btn btn__floating btn__round__large btn__round__large">
      <va-popover
        :message="$t('clusters.new_cluster')"
        placement="left"
      >
        <va-button
          @click.prevent="onClickCreateCluster">
          <i class="fa fa-plus"></i>
        </va-button>
      </va-popover>
    </div>

    <v-server-table
      id="clustersTable"
      ref="clustersTable"
      :columns="columns"
      :options="tableOptions"
      @row-click="showCluster"
    >
      <span slot="entity" slot-scope="{row}">
        {{ formatEntities(row.entities) }}
      </span>
      <span slot="entities" slot-scope="{row}">
        {{ row.entities.length }}
      </span>
      <div slot="users" slot-scope="{row}" class="text-center">
        {{ row.users.length }}
      </div>
      <div slot="actions" slot-scope="{row}" class="text-center">
        <span v-if="clusterContainsUser(row)">
          <va-popover :message="$t('clusters.buttons.leave_cluster')" placement="left">
            <i class="fa fa-share-square-o p-2" @click.prevent.stop="onClickLeaveCluster(row)"></i>
          </va-popover>
        </span>
        <span v-else class="icon-placeholder">

        </span>

        <va-popover :message="$t('clusters.buttons.update_cluster')" placement="left" >
          <i class="fa fa-pencil p-2" @click.prevent.stop="setClusterToUpdate(row)"></i>
        </va-popover>

        <va-popover v-if="isFromEvollu" :message="$t('clusters.buttons.delete_cluster')" placement="left">
          <i class="fa fa-times-circle p-2" @click.prevent.stop="onClickDeleteCluster(row)"></i>
        </va-popover>
      </div>
    </v-server-table>

    <ShowClusterModal
      v-if="isShowingCluster"
      :cluster="selectedClusterToShow"
      @cancel="onCloseShowClusterModal"
      @clusterUpdated="onClusterUpdatedFromShow"
    >
    </ShowClusterModal>

    <CreateUpdateClusterModal
      v-if="isCreatingOrUpdatingCluster"
      :cluster="selectedClusterToUpdate"
      @clusterCreated="onClusterCreated"
      @clusterUpdated="onClusterUpdated"
      @cancel="onCloseCreateOrUpdateModal"
    >
    </CreateUpdateClusterModal>

    <ConfirmationModal v-if="isDeletingCluster"
      :title="$t('clusters.delete_cluster')"
      :body="$t('clusters.labels.delete_modal_body', {name: selectedClusterToDelete.name})"
      okClass="btn-danger"
      @modal-confirmed="onConfirmDeleteCluster"
      @modal-canceled="onCancelDeleteCluster"
    >
    </ConfirmationModal>

    <ConfirmationModal v-if="isLeavingCluster"
      :title="$t('clusters.labels.leave_cluster')"
      :body="$t('clusters.labels.leave_modal_body', {name: selectedClusterToLeave.name})"
      @modal-confirmed="onConfirmLeaveCluster"
      @modal-canceled="onCancelLeaveCluster"
    >
    </ConfirmationModal>
  </section>
</template>

<script>
import Breadcrumbs from '@/components/common/Breadcrumbs'
import Searchable from '@/components/common/Searchable'
import CreateUpdateClusterModal from './CreateUpdateClusterModal'
import ShowClusterModal from './ShowClusterModal'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import TableQueryMixin from '@/mixins/TableQueryMixin'
import IsFromEvolluMixin from '@/mixins/IsFromEvolluMixin'
import { ClusterService } from '@/api/AuthService'

export default {
  props: {
    component: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    }
  },
  components: {
    Searchable,
    Breadcrumbs,
    ConfirmationModal,
    ShowClusterModal,
    CreateUpdateClusterModal,
  },
  mixins: [
    TableQueryMixin,
    IsFromEvolluMixin
  ],
  data () {
    return {
      breadcrumbs: [
        { title: this.$t('menu.configurations'), disabled: true },
        { title: this.$t('menu.clusters'), disabled: true }
      ],
      querySearch: '',
      isCreatingOrUpdatingCluster: false,
      isLeavingCluster: false,
      isDeletingCluster: false,
      selectedClusterToUpdate: null,
      selectedClusterToShow: null,
      selectedClusterToDelete: null,
      selectedClusterToLeave: null,
      isShowingCluster: null,
      columns: ['name', 'entity', 'entities', 'users', 'actions'],
      queryUrl: `${this.component.service.url}/clusters`,
      tableOptions: {
        sortable: ['name', 'users'],
        saveState: true,
        headings: {
          'name': this.$t('users.inputs.name'),
          'entity': this.$t('clusters.labels.entities'),
          'entities': this.$t('clusters.labels.number_entities'),
          'users': this.$t('clusters.labels.number_users'),
          'actions': this.$t('clusters.labels.actions')
        },
        columnsClasses: {
          name: 'name',
          entity: 'entity',
          entities: 'entities',
          users: 'users',
          actions: 'actions',
        }
      }
    }
  },
  methods: {
    getTableReference: function () {
      return this.$refs.clustersTable
    },
    onClickLeaveCluster: function (cluster) {
      this.isLeavingCluster = true
      this.selectedClusterToLeave = cluster
    },
    onCancelLeaveCluster: function () {
      this.isLeavingCluster = false
      this.selectedClusterToLeave = null
    },
    onClickCreateCluster: function () {
      this.isCreatingOrUpdatingCluster = true
    },
    showCluster: function (selectedRow) {
      this.isShowingCluster = true
      this.selectedClusterToShow = selectedRow.row
    },
    onCloseShowClusterModal: function () {
      this.isShowingCluster = false
      this.selectedClusterToShow = null
    },
    setClusterToUpdate: function (cluster) {
      this.selectedClusterToUpdate = cluster
      this.isCreatingOrUpdatingCluster = true
    },
    onCloseCreateOrUpdateModal: function () {
      this.selectedClusterToUpdate = null
      this.isCreatingOrUpdatingCluster = false
    },
    onClusterCreated: function ({ cluster }) {
      this.$refs.clustersTable.refresh()
      this.isCreatingOrUpdatingCluster = false
    },
    onClusterUpdated: function ({ cluster }) {
      this.$refs.clustersTable.refresh()
      this.isCreatingOrUpdatingCluster = false
    },
    onClusterUpdatedFromShow: function ({ cluster }) {
      this.$refs.clustersTable.refresh()
      this.selectedClusterToShow = cluster
    },
    onClickDeleteCluster: function (cluster) {
      this.isDeletingCluster = true
      this.selectedClusterToDelete = cluster
    },
    onCancelDeleteCluster: function () {
      this.isDeletingCluster = false
      this.selectedClusterToDelete = null
    },
    onConfirmDeleteCluster: function () {
      ClusterService.delete(this.selectedClusterToDelete.id)
        .then(response => {
          this.$refs.clustersTable.refresh()
          this.showToast(this.$t('clusters.messages.successful_delete'), this.$store.state.successToastOptions)
          this.onCancelDeleteCluster()
        })
        .catch(errors => this.showToast(this.$t('clusters.messages.something_went_wrong'), this.$store.state.errorToastOptions))
    },
    onConfirmLeaveCluster: function () {
      ClusterService.leave(this.selectedClusterToLeave.id)
        .then(response => {
          console.log(response)
          this.$refs.clustersTable.refresh()
          this.showToast(this.$t('clusters.messages.successful_leave'), this.$store.state.successToastOptions)
          this.onCancelLeaveCluster()
        })
        .catch(errors => {
          console.log(errors)
          this.showToast(this.$t('clusters.messages.something_went_wrong'), this.$store.state.errorToastOptions)
        })
    },
    formatEntities: function (entities) {
      if (entities.length > 0) {
        let formatedString = entities.map(e => e.name).join(', ')
        if (formatedString.length > 10) {
          return formatedString.substring(0, 70) + '...'
        }
        return formatedString
      } else {
        return this.$t('clusters.labels.no_entities_associated')
      }
    },
    clusterContainsUser: function (cluster) {
      return cluster.userIds.includes(this.$auth.user().id)
    },
  },
  watch: {
    'querySearch': function (value) {
      this.$refs.clustersTable.setFilter(value)
    }
  }
}
</script>

<style lang="scss">
.icon-placeholder {
  display:inline-block;
  width: 30.5px !important;
}

#clusters-index {
  th.entities {
    text-align: center;
    min-width: 105px;
  }

  td.entities {
    text-align: center;
  }

  th.users {
    text-align: center;
    min-width: 120px;
  }

  td.users {
    text-align: center;
  }

  th.actions {
    min-width: 120px;
    text-align: center;
  }
}
</style>
